import { Badge } from '@/components/ui/badge';
import { Mail, Phone } from 'lucide-react';
import { TEAM_IMAGES } from './teamImages';

const chairmanMessage = {
  name: "Abinash Karn",
  title: "Chairman, Track0tic",
  message: `Track0tic was conceived with a simple but powerful belief: every medicine delivery should be verifiable, compliant, and trustworthy. By combining intelligent sensors, real-time monitoring, and advanced analytics, Track0tic brings a new level of accountability to pharmaceutical logistics — enabling stakeholders to ensure that medicines maintain their required conditions and reach their destination safely.

Our vision extends beyond tracking shipments. We aim to build a system that provides true delivery verification — confirming not only where a package traveled, but whether it was handled correctly, protected from tampering, and maintained within required environmental conditions.

When medicine integrity is protected, patient safety is protected.`
};

const teamMembers = [
  {
    name: "Rackesh Langer",
    role: "Mentor & Advisor",
    title: "Mentor, Guidance & PCB Design",
    bio: "A graduate of Birla Institute of Technology, Mesra (1977), Rackesh brings over 45 years of progressive expertise across process control instrumentation, enterprise computing, LAN/WAN networking, and embedded systems. He provides strategic mentorship to Track0tic and personally leads the design and development of the project's custom PCB hardware.",
    skills: ["PCB Design", "Embedded Systems", "Instrumentation", "Networking", "Mentorship"],
    experience: "45+",
    education: "BIT Mesra, 1977",
    email: "Rackesh.langer@trackotic.com",
    phone: null,
    imageKey: "rackesh",
    color: "from-blue-500 to-indigo-600"
  },
  {
    name: "Deepak Kumar",
    role: "Backend Lead",
    title: "Sr. Backend & Full-Stack Developer",
    bio: "A seasoned backend architect with 15+ years delivering high-performance enterprise systems. Expert in Node.js, PHP (Laravel/CodeIgniter), MySQL, and RESTful APIs. Has driven backend solutions across fintech, pharma, and logistics — currently engineering Track0tic's core platform and data infrastructure.",
    skills: ["Node.js", "PHP / Laravel", "MySQL", "REST APIs", "CI/CD"],
    experience: "15+",
    education: "BIT, IGNOU Patna",
    email: "Deepak.kumar@trackotic.com",
    phone: "+91-9711086568",
    imageKey: "deepak",
    color: "from-emerald-500 to-teal-600"
  },
  {
    name: "Sarthak Srivastav",
    role: "Mobile Lead",
    title: "Lead Android / Mobile Engineer",
    bio: "Android specialist with 5+ years of experience, currently leading mobile development for Punjab National Bank under Nityo Infotech & Infosys. Expert in Kotlin, Jetpack Compose, MVVM, and fintech-grade app security. Brings deep mobile expertise to Track0tic's patient-facing and logistics apps.",
    skills: ["Kotlin / Java", "Jetpack Compose", "MVVM", "Fintech", "Play Store"],
    experience: "5+",
    education: "B.Tech, RIT",
    email: "Sarthak.Srivastav@trackotic.com",
    phone: "8103180989",
    imageKey: "sarthak",
    color: "from-orange-500 to-red-600"
  }
];

export default function OpsTeamPage() {
  return (
    <div data-testid="ops-team-page" className="min-h-screen bg-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          Our Team
        </h1>
        <p className="text-slate-400">
          The people driving Track<span className="text-red-500">0</span>tic's mission
        </p>
      </div>

      {/* Chairman's Message */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden mb-8" data-testid="chairman-section">
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-3">
          <h2 className="text-lg font-semibold text-white">Chairman's Message</h2>
        </div>
        <div className="p-6">
          <div className="text-slate-300 leading-relaxed space-y-4">
            {chairmanMessage.message.split('\n\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-slate-800 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white font-bold text-lg">
              AK
            </div>
            <div>
              <div className="font-semibold text-white">{chairmanMessage.name}</div>
              <div className="text-sm text-slate-400">
                Chairman, Track<span className="text-red-500">0</span>tic
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Members Grid */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-white mb-4">Meet Our Team</h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="team-members-section">
        {teamMembers.map((member, index) => (
          <div
            key={member.name}
            className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden hover:border-slate-700 transition-colors"
            data-testid={`team-member-${index}`}
          >
            {/* Header with gradient */}
            <div className={`relative h-28 bg-gradient-to-br ${member.color}`}>
              <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2">
                <div className="w-20 h-20 rounded-full border-4 border-slate-900 overflow-hidden shadow-lg bg-slate-800">
                  <img 
                    src={TEAM_IMAGES[member.imageKey]} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="pt-14 pb-5 px-5 text-center">
              <Badge className="mb-2 bg-slate-800 text-slate-300 border-slate-700 text-xs">
                {member.role}
              </Badge>
              <h3 className="text-lg font-bold text-white mb-1">
                {member.name}
              </h3>
              <p className="text-xs text-slate-400 mb-3">{member.title}</p>
              
              <p className="text-sm text-slate-400 leading-relaxed mb-4 line-clamp-3">
                {member.bio}
              </p>

              {/* Skills */}
              <div className="flex flex-wrap justify-center gap-1.5 mb-4">
                {member.skills.slice(0, 4).map((skill) => (
                  <span 
                    key={skill}
                    className="text-xs px-2 py-0.5 bg-teal-900/30 text-teal-400 rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>

              {/* Experience Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-full mb-4">
                <span className="text-xl font-bold text-white">{member.experience}</span>
                <span className="text-xs text-slate-400 text-left leading-tight">
                  Years Exp.
                </span>
                <span className="text-xs text-slate-500">• {member.education}</span>
              </div>

              {/* Contact Links */}
              <div className="flex justify-center gap-2 pt-3 border-t border-slate-800">
                {member.email && (
                  <a 
                    href={`mailto:${member.email}`}
                    className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:bg-teal-900/30 hover:text-teal-400 transition-colors"
                    title={member.email}
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                )}
                {member.phone && (
                  <a 
                    href={`tel:${member.phone}`}
                    className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:bg-green-900/30 hover:text-green-400 transition-colors"
                    title={member.phone}
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Join Us Section */}
      <div className="mt-8 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl border border-slate-700 p-6 text-center">
        <h3 className="text-xl font-bold text-white mb-2">
          Join the Track<span className="text-red-500">0</span>tic Team
        </h3>
        <p className="text-slate-400 mb-4 max-w-lg mx-auto">
          We're always looking for talented individuals passionate about healthcare technology and making a real impact on patient safety.
        </p>
        <a 
          href="mailto:careers@trackotic.com"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors"
        >
          <Mail className="w-4 h-4" />
          careers@trackotic.com
        </a>
      </div>
    </div>
  );
}
